import { useTranslation } from 'react-i18next';
import {
  StyledClipLoaderWrapper,
  StyledExecutionLoader,
  StyledHeaderButtons,
  StyledMinimizedButton,
  StyledSQLBoardHeader,
  StyledSQLBoardHeaderControls,
  StyledSQLBoardHeaderResults,
  StyledSQLResultsButtons,
  StyledSQLScriptMessage,
  StyledSQLScriptResults,
} from './styles';
import {
  SMART_CONNECTOR_STATUSES,
  SQL_SCRIPT_STATUSES,
} from 'pages/SmartConnectors/constants';
import {
  SQLScript,
  SQLScriptStatus,
  ScriptExecutionResult,
  SmartConnector,
} from 'pages/SmartConnectors/types';
import ToggleButton from '__components/Kizen/ToggleButton';
import Button from '__components/Button';
import ConfirmationModal from '__components/Modals/ConfirmationModal';
import useModal from '__components/Modals/useModal';
import {
  getScriptResultMessage,
  getSmartConnectorDTO,
  toHtml,
} from 'pages/SmartConnectors/SmartConnector/helpers';
import { useContext } from 'react';
import { SmartConnectorContext } from '../../context';
import SmartConnectorService from 'services/SmartConnectorService';
import { useToast } from '__components/ToastProvider';
import { toastVariant } from '__components/ToastProvider';
import { getIsSmartConnectorActive } from 'pages/SmartConnectors/helpers';
import { downloadFile } from 'utility/DownloadFile';
import { ClipLoader } from 'react-spinners';
import { SQLVersionSelect } from './SQLVersionSelect';

type SQLBoardHeaderProps = {
  view: SQLScriptStatus;
  onChangeView: (view: SQLScriptStatus) => void;
  handleTestExecution: () => Promise<void>;
  handlePublishLiveScript: () => Promise<boolean>;
  handleDownloadLocalDevPackage: () => Promise<void>;
  executionResult: ScriptExecutionResult | null;
  pollingScriptExecution: boolean;
  pollingDevPackage: boolean;
  publishingLiveScript: boolean;
  liveScript: SQLScript | null;
  isDraftScriptDirty: boolean;
  handleResetToLive: () => void;
};

export const SQLScriptsHeader = ({
  view,
  onChangeView,
  handleTestExecution,
  handlePublishLiveScript,
  handleDownloadLocalDevPackage,
  executionResult,
  pollingScriptExecution,
  pollingDevPackage,
  publishingLiveScript,
  liveScript,
  isDraftScriptDirty,
  handleResetToLive,
}: SQLBoardHeaderProps) => {
  const { t } = useTranslation();
  const [showToast] = useToast();

  const { smartConnector, stepData, handleUpdate } = useContext(
    SmartConnectorContext
  );

  const options = [
    { value: SQL_SCRIPT_STATUSES.template, label: t('Template') },
    { value: SQL_SCRIPT_STATUSES.draft, label: t('Draft') },
    { value: SQL_SCRIPT_STATUSES.live, label: t('Live') },
  ];

  const handleChangeView = ({ value }: { value: SQLScriptStatus }) =>
    onChangeView(value);

  const handleDownloadLiveOutput = () => {
    if (liveScript?.output_csv_file?.url) {
      downloadFile(liveScript.output_csv_file.url);
    }
  };

  const handleDownloadDraftOutput = () => {
    if (executionResult?.export_file?.url) {
      downloadFile(executionResult.export_file.url);
    }
  };

  const resultMessage = getScriptResultMessage(executionResult, t);

  const [
    publishConfirmationModalProps,
    ,
    { show: handleShowPublishConfirmationModal },
  ] = useModal({
    handleSubmit: async () => {
      if (!isDraftScriptDirty) {
        const published = await handlePublishLiveScript();
        if (published) {
          onChangeView(SQL_SCRIPT_STATUSES.live);
        }
      }
    },
  });

  const [
    deactivateConfirmationModalProps,
    ,
    { show: handleShowDeactivationConfirmationModal },
  ] = useModal({
    handleSubmit: async () => {
      const promise = new Promise<SmartConnector>((resolve, reject) => {
        SmartConnectorService.updateSmartConnector(smartConnector.id, {
          ...getSmartConnectorDTO(stepData),
          status: SMART_CONNECTOR_STATUSES.inactive,
        })
          .then(({ data }) => {
            handleShowPublishConfirmationModal();
            resolve(data);
          })
          .catch((error) => {
            showToast({
              message: t('Failed to deactivate SmartConnector'),
              variant: toastVariant.FAILURE,
            });
            reject(error);
          });
      });
      await handleUpdate(promise);
    },
  });

  const [
    resetConfirmationModalProps,
    ,
    { show: handleShowResetConfirmationModal },
  ] = useModal({
    handleSubmit: handleResetToLive,
  });

  const executionError =
    !!executionResult?.meta && 'error' in executionResult.meta;

  const onClickPublish = () => {
    if (getIsSmartConnectorActive(smartConnector)) {
      handleShowDeactivationConfirmationModal();
    } else {
      handleShowPublishConfirmationModal();
    }
  };

  return (
    <>
      <StyledSQLBoardHeader>
        <StyledSQLBoardHeaderControls>
          <ToggleButton
            options={options}
            value={view}
            onChange={handleChangeView}
          />
          {view !== SQL_SCRIPT_STATUSES.template &&
          (view === SQL_SCRIPT_STATUSES.draft ||
            stepData.live_script?.sql_version) ? (
            <SQLVersionSelect view={view} />
          ) : null}
          {view !== SQL_SCRIPT_STATUSES.template &&
          stepData.sql_enabled &&
          stepData.source_file ? (
            <StyledMinimizedButton
              variant="text"
              color="blue"
              onClick={handleDownloadLocalDevPackage}
              loading={pollingDevPackage}
            >
              {pollingDevPackage ? (
                <StyledClipLoaderWrapper>
                  <ClipLoader
                    loading
                    size={20}
                    color="var(--color-button-secondary-hover)"
                  />
                </StyledClipLoaderWrapper>
              ) : null}
              {t('Download Dev Package')}
            </StyledMinimizedButton>
          ) : null}
          <StyledHeaderButtons>
            {view === SQL_SCRIPT_STATUSES.draft ? (
              <>
                {liveScript ? (
                  <Button
                    onClick={handleShowResetConfirmationModal}
                    color="red"
                    variant="text"
                  >
                    {t('Reset to Live')}
                  </Button>
                ) : null}
                <Button
                  onClick={handleTestExecution}
                  color="blue"
                  disabled={pollingScriptExecution || publishingLiveScript}
                >
                  {t('Test Execution')}
                </Button>
              </>
            ) : null}
            {view === SQL_SCRIPT_STATUSES.live && liveScript ? (
              <Button
                onClick={handleDownloadLiveOutput}
                color="blue"
                variant="text"
              >
                {t('Download SQL Output')}
              </Button>
            ) : null}
          </StyledHeaderButtons>
        </StyledSQLBoardHeaderControls>
        {(executionResult || pollingScriptExecution) &&
        view === SQL_SCRIPT_STATUSES.draft ? (
          <StyledSQLBoardHeaderResults>
            <StyledExecutionLoader loading={pollingScriptExecution} size={32} />
            {pollingScriptExecution ? (
              <StyledSQLScriptMessage>
                {t(
                  'Your SQL is being executed and output table generated. A status will appear here when execution has completed.'
                )}
              </StyledSQLScriptMessage>
            ) : (
              <StyledSQLScriptResults
                key={resultMessage}
                enableToolbar={false}
                enableResize
                initialValue={toHtml(resultMessage)}
                initialHeight={55}
                disabled
              />
            )}
            <StyledSQLResultsButtons>
              <Button
                variant="text"
                color="blue"
                onClick={handleDownloadDraftOutput}
                disabled={
                  pollingScriptExecution || !executionResult?.export_file?.url
                }
              >
                {t('Download SQL Output')}
              </Button>
              <Button
                onClick={onClickPublish}
                disabled={
                  pollingScriptExecution ||
                  executionError ||
                  publishingLiveScript
                }
              >
                {t('Publish Live')}
              </Button>
            </StyledSQLResultsButtons>
          </StyledSQLBoardHeaderResults>
        ) : null}
      </StyledSQLBoardHeader>
      <ConfirmationModal
        heading={
          isDraftScriptDirty ? t('Cannot Publish') : t('Confirm Publish')
        }
        {...publishConfirmationModalProps}
        leftBtn={isDraftScriptDirty ? null : undefined}
        actionBtnColor={isDraftScriptDirty ? 'blue' : 'green'}
        buttonText={isDraftScriptDirty ? t('Close') : t('Confirm')}
      >
        {isDraftScriptDirty
          ? t(
              'The current draft state has not been successfully tested. You must test the execution before publishing.'
            )
          : t(
              'Publishing your changes will overwrite your existing SmartConnector processing. Do you wish to continue?'
            )}
      </ConfirmationModal>
      <ConfirmationModal
        heading={t('You Have Unpublished Changes')}
        {...resetConfirmationModalProps}
        actionBtnColor="red"
        buttonText={t('Discard Changes')}
      >
        {t('Unpublished changes will be lost, would you like to continue?')}
      </ConfirmationModal>
      <ConfirmationModal
        {...deactivateConfirmationModalProps}
        buttonText={t('Disable and Continue')}
      >
        {t(
          'This action will disable the connector, would you like to continue?'
        )}
      </ConfirmationModal>
    </>
  );
};
